import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
} from '@mui/material';
import { accountingService } from '../services/accountingService';
import { EmployeePettyCashSummary } from '../types/accounting';
import { useAuth } from '../hooks/UserContext';

const PettyCashPage: React.FC = () => {
    const [summaries, setSummaries] = useState<EmployeePettyCashSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const { user } = useAuth();

    useEffect(() => {
        const fetchSummaries = async () => {
            try {
                setLoading(true);
                const data = await accountingService.getEmployeePettyCashSummary();
                setSummaries(data);
                setError(null);
            } catch (err) {
                setError('Failed to fetch petty cash summaries.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (user?.role === 'OWNER' || user?.role === 'MANAGER') {
            fetchSummaries();
        }
    }, [user]);

    const handleRowClick = (userId: number) => {
        navigate(`/petty-cash/${userId}`);
    };

    if (user?.role !== 'OWNER' && user?.role !== 'MANAGER') {
        return <Typography>You are not authorized to view this page.</Typography>;
    }

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4">Employee Petty Cash</Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Employee Name</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Details</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {summaries.map((summary) => (
                            <TableRow
                                key={summary.id}
                                hover
                                onClick={() => handleRowClick(summary.id)}
                                sx={{ cursor: 'pointer' }}
                            >
                                <TableCell>{`${summary.first_name} ${summary.last_name}`}</TableCell>
                                <TableCell sx={{ textTransform: 'capitalize' }}>{summary.role.replace('_', ' ').toLowerCase()}</TableCell>
                                <TableCell>
                                    Click to view details
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default PettyCashPage;
